/**
 * Vector Capital + — multi-bucket portfolio allocator.
 *
 * Where the original Vector Capital page runs a SINGLE equity-curve strategy
 * standalone (one allocation slider), Vector Capital + combines MANY buckets at
 * once. Each bucket is one equity-curve strategy with:
 *   - weightPct : the bucket's share of the TOTAL portfolio (% of capital base)
 *   - exposure  : a per-bucket risk/leverage dial applied to that bucket's daily
 *                 returns (1 = as-is, 0.4 = "take this position down", 1.5 = lever up)
 *
 * This is what Max asked for: per-asset-class weights the user can move around,
 * plus independent risk per bucket — e.g. size the futures bucket to ~1% of the
 * whole portfolio rather than running the same risk across everything. Nesting
 * (e.g. 5 stocks at 5% each inside a 25% "stocks" sleeve) is just 5 buckets at
 * 5% — no special UI needed.
 *
 * The scenario value each day = uninvested cash + the sum of every bucket's
 * compounded slice. Buckets only move on dates present in their own return
 * series, so asset classes with different trading calendars (crypto weekends vs
 * futures weekdays) align naturally by holding flat on missing days. An optional
 * periodic rebalance resets buckets back to their target weights.
 */

const DEFAULT_BASE_VALUE = 100000;

function toFraction(weightPct) {
  const n = Number(weightPct);
  return Number.isFinite(n) ? Math.max(n, 0) / 100 : 0;
}

function safeExposure(exposure) {
  const n = Number(exposure);
  if (!Number.isFinite(n) || n < 0) return 1;
  return n;
}

/** Period bucket key for rebalancing; null = no rebalance (buy-and-hold drift). */
function periodKeyFor(dateStr, frequency) {
  if (frequency === 'monthly') return String(dateStr).slice(0, 7); // YYYY-MM
  if (frequency === 'quarterly') {
    const year = String(dateStr).slice(0, 4);
    const month = parseInt(String(dateStr).slice(5, 7), 10);
    const quarter = Math.floor((month - 1) / 3) + 1;
    return `${year}-Q${quarter}`;
  }
  if (frequency === 'yearly') return String(dateStr).slice(0, 4);
  return null;
}

/**
 * Build a daily portfolio series (same point shape as buildPortfolioDailySeries /
 * buildEquityStrategyDailySeries) from multiple weighted, risk-scaled buckets.
 *
 * @param {Array<{ returns: Array<{date: string, ret: number}>, weightPct: number, exposure: number }>} buckets
 * @param {number} baseValue total capital base
 * @param {object} [opts]
 * @param {'none'|'monthly'|'quarterly'|'yearly'} [opts.rebalance] rebalance cadence
 * @param {boolean} [opts.renormalizeStarts] until a bucket's data begins, share its
 *   weight pro-rata across the live buckets; rebalance the pot to target weights
 *   on each bucket's first data date (Custom "Renormalized weights" start basis)
 * @returns {Array<{date, dailyPnl, cumulative, normalized, dailyReturnPct}>}
 */
export function buildMultiBucketDailySeries(buckets, baseValue = DEFAULT_BASE_VALUE, opts = {}) {
  const rebalance = opts.rebalance || 'none';
  const renormalizeStarts = opts.renormalizeStarts === true;
  const prepared = (Array.isArray(buckets) ? buckets : [])
    .map((bucket) => {
      const weight = toFraction(bucket?.weightPct);
      if (weight <= 0) return null;
      const retByDate = new Map();
      const returns = Array.isArray(bucket?.returns) ? bucket.returns : [];
      returns.forEach((point) => {
        if (point && typeof point.date === 'string' && Number.isFinite(Number(point.ret))) {
          retByDate.set(point.date, Number(point.ret));
        }
      });
      if (retByDate.size === 0) return null;
      return {
        weight,
        exposure: safeExposure(bucket?.exposure),
        retByDate,
        // compound: true (default) multiplies the slice by (1+ret) daily —
        // correct for ratio returns from real compounding curves (Kairos).
        // compound: false accumulates ret × the slice's allocated notional —
        // correct for fixed-notional returns (P&L ÷ base); compounding those
        // over years explodes exponentially vs the strategy's additive curve.
        compound: bucket?.compound !== false,
        value: baseValue * weight,
        notional: baseValue * weight,
      };
    })
    .filter(Boolean);

  if (!prepared.length) return [];

  const sumWeights = prepared.reduce((sum, bucket) => sum + bucket.weight, 0);
  let cash = baseValue * (1 - sumWeights); // negative => simple margin; stays flat

  if (renormalizeStarts) {
    // Buckets start empty and activate on their first data date; until then the
    // whole pot rides in cash and gets (re)distributed among live buckets only.
    prepared.forEach((bucket) => {
      let first = null;
      bucket.retByDate.forEach((_ret, date) => { if (!first || date < first) first = date; });
      bucket.firstDate = first;
      bucket.live = false;
      bucket.value = 0;
    });
    cash = baseValue;
  }

  /** Distribute `total` to live buckets at their renormalized target weights. */
  const redistributeToLive = (total) => {
    const liveWeight = prepared.reduce((sum, b) => sum + (b.live ? b.weight : 0), 0);
    if (liveWeight <= 0) return;
    prepared.forEach((b) => {
      b.value = b.live ? total * (b.weight / liveWeight) * sumWeights : 0;
      b.notional = b.value;
    });
    cash = total * (1 - sumWeights);
  };

  const dateSet = new Set();
  prepared.forEach((bucket) => {
    bucket.retByDate.forEach((_ret, date) => dateSet.add(date));
  });
  const dates = Array.from(dateSet).sort((a, b) => a.localeCompare(b));
  if (!dates.length) return [];

  let prevEquity = baseValue;
  let currentPeriod = periodKeyFor(dates[0], rebalance);

  return dates.map((date) => {
    // Activate buckets whose data starts today: rebalance the whole pot to the
    // renormalized target weights across the (now larger) live set.
    if (renormalizeStarts) {
      const coming = prepared.filter((b) => !b.live && b.firstDate && b.firstDate <= date);
      if (coming.length) {
        const total = cash + prepared.reduce((sum, bucket) => sum + bucket.value, 0);
        coming.forEach((b) => { b.live = true; });
        redistributeToLive(total);
      }
    }

    // Rebalance to target weights at the first day of each new period.
    if (rebalance !== 'none') {
      const periodKey = periodKeyFor(date, rebalance);
      if (periodKey !== currentPeriod) {
        const total = cash + prepared.reduce((sum, bucket) => sum + bucket.value, 0);
        if (renormalizeStarts) {
          redistributeToLive(total);
        } else {
          prepared.forEach((bucket) => {
            bucket.value = total * bucket.weight;
            bucket.notional = bucket.value;
          });
          cash = total * (1 - sumWeights);
        }
        currentPeriod = periodKey;
      }
    }

    const startingEquity = prevEquity;
    prepared.forEach((bucket) => {
      const ret = bucket.retByDate.get(date);
      if (ret == null) return; // bucket holds flat on days it doesn't trade
      if (bucket.compound) {
        bucket.value = Math.max(bucket.value * (1 + ret * bucket.exposure), 0);
      } else {
        // Fixed-notional: each day's P&L is ret × the slice's allocated
        // notional — mirrors the strategy's own additive $100k-base curve.
        bucket.value += bucket.notional * ret * bucket.exposure;
      }
    });

    const equity = cash + prepared.reduce((sum, bucket) => sum + bucket.value, 0);
    const dailyPnl = equity - startingEquity;
    prevEquity = equity;

    return {
      date,
      dailyPnl: Math.round(dailyPnl * 100) / 100,
      cumulative: Math.round((equity - baseValue) * 100) / 100,
      normalized: Math.round(equity * 100) / 100,
      dailyReturnPct: startingEquity > 0
        ? Math.round(((dailyPnl / startingEquity) * 100) * 10000) / 10000
        : 0,
    };
  });
}

/** Sum of bucket weights as a percentage (for the "X% allocated / Y% cash" readout). */
export function totalBucketWeightPct(buckets) {
  return (Array.isArray(buckets) ? buckets : []).reduce((sum, bucket) => {
    const n = Number(bucket?.weightPct);
    return sum + (Number.isFinite(n) ? Math.max(n, 0) : 0);
  }, 0);
}
